import { z } from 'zod'
import {
  ApiCollectionResponseSchema,
  ApiPaginatedResponseSchema,
  ApiSingleResponseSchema,
} from './api'
import type {
  ApiCollectionResponse,
  ApiPaginatedResponse,
  ApiSingleResponse,
} from './api'
import { GenericCompanySchema } from './generics'
import { SubscriptionSchema } from './subscription'
import { UserReferenceSchema } from './user'
import { SubscriberSettingsSchema } from './subscriberSettings'

// =====================================
// RELATED SCHEMAS
// =====================================

// Statistics Schema
export const SubscriberStatisticsSchema = z.object({
  clients: z
    .object({
      total: z.number(),
      active: z.number(),
    })
    .optional(),
  users: z
    .object({
      total: z.number(),
      active: z.number(),
      by_role: z.record(z.string(), z.number()).optional(),
    })
    .optional(),
  workers: z
    .object({
      total: z.number(),
    })
    .optional(),
  subscription: z
    .object({
      active: z.boolean(),
      expires_at: z.string().nullable(),
      days_remaining: z.number().nullable(),
    })
    .optional(),
})

// =====================================
// MAIN SUBSCRIBER SCHEMA
// =====================================

export const SubscriberSchema = GenericCompanySchema.extend({
  licence_no: z.string().max(32).nullable().optional(),
  licence_issued_at: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  logo: z
    .object({
      horizontal: z.object({
        exists: z.boolean(),
        url: z.string().nullable(),
        thumb_url: z.string().nullable(),
      }),
      icon: z.object({
        exists: z.boolean(),
        url: z.string().nullable(),
        favicon_url: z.string().nullable(),
      }),
    })
    .optional(),
  is_active: z.boolean().default(true),

  // Relations
  active_subscription: SubscriptionSchema.nullable().optional(),
  subscriptions: z.array(SubscriptionSchema).optional(),
  settings: SubscriberSettingsSchema.nullable().optional(),
  created_by: UserReferenceSchema.optional(),
  updated_by: UserReferenceSchema.optional(),

  // Counts
  clients_count: z.number().optional(),
  users_count: z.number().optional(),

  // Statistics
  statistics: SubscriberStatisticsSchema.optional(),

  // Timestamps - make optional since API doesn't always return them
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
})

export type Subscriber = z.infer<typeof SubscriberSchema>

export type SubscriberStatistics = z.infer<typeof SubscriberStatisticsSchema>

// =====================================
// API RESPONSE SCHEMAS
// =====================================

// Single subscriber response: { data: Subscriber }
export const SubscriberApiResponseSchema =
  ApiSingleResponseSchema(SubscriberSchema)
export type SubscriberApiResponse = ApiSingleResponse<Subscriber>

// Multiple subscribers response: { data: Subscriber[] }
export const SubscribersApiResponseSchema =
  ApiCollectionResponseSchema(SubscriberSchema)
export type SubscribersApiResponse = ApiCollectionResponse<Subscriber>

// Paginated subscribers response: { data: Subscriber[], meta: {...} }
export const SubscribersPaginatedApiResponseSchema =
  ApiPaginatedResponseSchema(SubscriberSchema)
export type SubscribersPaginatedApiResponse = ApiPaginatedResponse<Subscriber>

// =====================================
// CRUD SCHEMAS
// =====================================

export const CreateSubscriberSchema = SubscriberSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  active_subscription: true,
  subscriptions: true,
  created_by: true,
  updated_by: true,
  clients_count: true,
  users_count: true,
  statistics: true,
})

export type CreateSubscriber = z.infer<typeof CreateSubscriberSchema>

export const UpdateSubscriberSchema = CreateSubscriberSchema.partial()

export type UpdateSubscriber = z.infer<typeof UpdateSubscriberSchema>

// =====================================
// VALIDATION UTILITIES
// =====================================

// Direct validation functions
export const validateSubscriber = (data: unknown): Subscriber => {
  return SubscriberSchema.parse(data)
}

export const validateCreateSubscriber = (data: unknown): CreateSubscriber => {
  return CreateSubscriberSchema.parse(data)
}

export const validateUpdateSubscriber = (data: unknown): UpdateSubscriber => {
  return UpdateSubscriberSchema.parse(data)
}

// API response validation functions
export const validateSubscriberApiResponse = (data: unknown): Subscriber => {
  const response = SubscriberApiResponseSchema.parse(data)
  return response.data
}

export const validateSubscribersPaginatedApiResponse = (
  data: unknown
): SubscribersPaginatedApiResponse => {
  return SubscribersPaginatedApiResponseSchema.parse(data)
}

// Safe validation functions
export const safeValidateSubscriber = (data: unknown) => {
  return SubscriberSchema.safeParse(data)
}

export const safeValidateSubscriberApiResponse = (data: unknown) => {
  const result = SubscriberApiResponseSchema.safeParse(data)
  if (result.success) {
    return { success: true, data: result.data.data }
  }
  return { success: false, error: result.error }
}

// =====================================
// FILTER SCHEMA
// =====================================

export const SubscriberFilterSchema = z.object({
  search: z.string().optional(),
  is_active: z.boolean().optional(),
  has_subscription: z.boolean().optional(),
  subscription_expires_soon: z.number().optional(), // days
})

export type SubscriberFilter = z.infer<typeof SubscriberFilterSchema>

// =====================================
// REFERENCE SCHEMA
// =====================================

export const SubscriberReferenceSchema = z.object({
  id: z.number(),
  name: z.string(),
  short_name: z.string().optional(),
  is_active: z.boolean(),
})

export type SubscriberReference = z.infer<typeof SubscriberReferenceSchema>
